import React from 'react';

import { RadioGroup, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { labels } from 'client/blocks/Settings/channels/labels';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { FormErrors } from 'client/hocs/types';
import { routes } from 'client/lib/routes';
import { AliceSkill, Channel, SmartHome } from 'client/model/skill';
import { AliceSkillFormSettings, BackendType, SmartHomeFormSettings } from 'client/types';
import { getIsYangoRequest } from 'client/utils';
import * as keyset from 'i18n/SmartHomeForm';

import styles from './BackendTypeField.module.scss';
import FunctionsContainer from './FunctionsContainer';
import { functionListErrorMessages, validateFunctionErrorMessages } from './texts';

const t = i18n(keyset);

interface Props<T extends AliceSkill | SmartHome> {
    onWebhookChange: (value: string) => void;
    onFunctionIdChange: (value: string) => void;
    onBackendTypeChange: (value: BackendType) => void;
    disabled?: boolean;
    errors: T extends AliceSkill ? FormErrors<AliceSkillFormSettings> : FormErrors<SmartHomeFormSettings>;
    values: T extends AliceSkill ? AliceSkillFormSettings : SmartHomeFormSettings;
    skill: T;
}

export function BackendTypeField<T extends AliceSkill | SmartHome>({
    onWebhookChange,
    onFunctionIdChange,
    onBackendTypeChange,
    disabled,
    errors,
    values,
    skill,
}: Props<T>) {
    const l = labels[skill.channel];

    const objFieldProps = {
        webhook: {
            documentationUrl:
                skill.channel === Channel.AliceSkill
                    ? routes.documentation.aliceSkills.webhookUrl
                    : routes.documentation.smartHome.webhookUrl,
            error: errors.uri,
            caption: t('backend-caption'),
        },
        function: {
            documentationUrl: '',
            error: '',
            caption: '',
        },
    };

    const displayCloudFunctions =
        __appConfig.app.displayCloudFunctions || Boolean(window.sessionStorage.getItem('__display-cloud-functions'));

    const displayCloudFunctionsSmartHome =
        !getIsYangoRequest() &&
        (__appConfig.app.displayCloudFunctionsSmartHome ||
            Boolean(window.sessionStorage.getItem('__display-smart-home-cloud-functions')));

    const backendType = values.backendType;
    const fieldProps = objFieldProps[backendType];

    if (skill.channel === Channel.SmartHome && !displayCloudFunctionsSmartHome) {
        return (
            <Field
                required
                fieldTitle={l.uri}
                error={errors.uri}
                fieldDescription={t('backend-caption')}
                // documentationUrl={routes.documentation.smartHome.webhookUrl}
            >
                <TextInput
                    className={styles.formElement}
                    value={values.uri}
                    onChange={(e) => onWebhookChange(e.target.value)}
                    type="url"
                    disabled={disabled}
                    size="xl"
                />
            </Field>
        );
    }

    if (skill.channel === Channel.AliceSkill && !displayCloudFunctions) {
        return (
            <Field
                required
                fieldTitle={l.uri}
                error={errors.uri}
                fieldDescription={t('backend-caption')}
                // documentationUrl={routes.documentation.aliceSkills.webhookUrl}
            >
                <TextInput
                    className={styles.formElement}
                    value={values.uri}
                    onChange={(e) => onWebhookChange(e.target.value)}
                    disabled={disabled}
                    type="url"
                    size="xl"
                />
            </Field>
        );
    }

    return (
        <div>
            <Field
                required
                fieldTitle="Backend"
                error={fieldProps.error as string}
                fieldDescription={fieldProps.caption}
                // documentationUrl={fieldProps.documentationUrl}
            >
                <RadioGroup
                    className={styles.formElement}
                    name="backendType"
                    value={backendType}
                    disabled={disabled}
                    onChange={(e) => onBackendTypeChange(e.currentTarget.value as BackendType)}
                    size="l"
                >
                    <RadioGroup.Option value="webhook">{l.uri}</RadioGroup.Option>
                    <RadioGroup.Option value="function">{l.functionId}</RadioGroup.Option>
                </RadioGroup>
                {backendType === 'webhook' ? (
                    <TextInput
                        className={styles.formElement}
                        value={values.uri}
                        onUpdate={onWebhookChange}
                        disabled={disabled}
                        type="url"
                        size="xl"
                    />
                ) : (
                    <div className={styles.functions}>
                        <FunctionsContainer
                            onFunctionIdChange={onFunctionIdChange}
                            disabled={disabled}
                            value={values.functionId}
                            renderValidationError={(code) => <div>{validateFunctionErrorMessages[code]}</div>}
                            renderFunctionsListError={(code) => <div>{functionListErrorMessages[code]}</div>}
                        />
                    </div>
                )}
            </Field>
        </div>
    );
}
